import type { Dispatch, MouseEvent, SetStateAction } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { useShortcut } from "@/lib"
import { useEscapeClearsSelection } from "@/lib/hooks/use-escape-clears-selection"

// Rows scrolling under a still pointer after a key press must not steal the cursor.
const MOUSE_IGNORE_MS = 300

export type RowClickAction = "open" | "toggle" | "range"

export function nextSelection(
	current: ReadonlySet<string>,
	id: string,
	checked: boolean,
): ReadonlySet<string> {
	if (current.has(id) === checked) return current
	const next = new Set(current)
	if (checked) next.add(id)
	else next.delete(id)
	return next
}

/** A plain click opens the row until something is checked, then it toggles; Shift-click selects a range. */
export function rowClickAction(
	event: Pick<MouseEvent, "shiftKey">,
	isSelecting: boolean,
): RowClickAction {
	if (event.shiftKey) return "range"
	return isSelecting ? "toggle" : "open"
}

/** Indices from anchor to target inclusive, or just the target without an anchor. */
export function rangeIndices(anchor: number | null, target: number): number[] {
	const start = anchor ?? target
	const [from, to] = start <= target ? [start, target] : [target, start]
	return Array.from({ length: to - from + 1 }, (_, i) => from + i)
}

// The cursor follows the pointer, so it stands in for a hover style.
export function rowHighlightClass({
	isCursor,
	isChecked,
}: {
	isCursor: boolean
	isChecked: boolean
}) {
	if (isCursor) return "bg-gray-100 dark:bg-gray-850/60"
	if (isChecked) return "bg-gray-50 dark:bg-gray-850/30"
	return undefined
}

/** The row a J/K step lands on: the first or last when there is no cursor, and it stops at either end. */
export function stepIndex(
	cursor: number | null,
	count: number,
	direction: 1 | -1,
): number | null {
	if (count === 0) return null
	if (cursor === null || cursor >= count) {
		return direction === 1 ? 0 : count - 1
	}
	return Math.min(count - 1, Math.max(0, cursor + direction))
}

/**
 * Inbox-style selection for a table: J/K and the arrows move the cursor, Cmd+arrows
 * jump to either end, X checks the cursor row, Shift+J/K extends, Enter opens, and
 * once a row is checked a click anywhere on a row toggles it.
 */
export function useListRowSelection({
	count,
	idAt,
	selectedIds,
	setSelectedIds,
	onClear,
	canSelect,
	scrollToIndex,
	openAt,
	resetKey,
}: {
	count: number
	// Undefined for a row the windowed list has not loaded yet.
	idAt: (index: number) => string | undefined
	selectedIds: ReadonlySet<string>
	setSelectedIds: Dispatch<SetStateAction<ReadonlySet<string>>>
	onClear: () => void
	canSelect: boolean
	scrollToIndex: (index: number) => void
	openAt: (index: number) => void
	// Indices point at different rows after a search, filter, or sort change.
	resetKey: string
}) {
	const [cursor, setCursorState] = useState<number | null>(null)
	// Read synchronously so a held key never steps from a stale index.
	const cursorRef = useRef<number | null>(null)
	const anchor = useRef<number | null>(null)
	const lastKeyAt = useRef(0)
	const cursorFromMouse = useRef(false)

	const setCursor = useCallback((index: number | null) => {
		cursorRef.current = index
		setCursorState(index)
	}, [])

	useEffect(() => {
		setCursor(null)
		anchor.current = null
	}, [resetKey, setCursor])

	const moveCursor = useCallback(
		(index: number | null) => {
			if (index === null) return
			lastKeyAt.current = Date.now()
			cursorFromMouse.current = false
			setCursor(index)
			scrollToIndex(index)
		},
		[scrollToIndex, setCursor],
	)

	const checkAt = useCallback(
		(index: number, checked: boolean) => {
			const id = idAt(index)
			if (!id) return
			anchor.current = index
			setSelectedIds((current) => nextSelection(current, id, checked))
		},
		[idAt, setSelectedIds],
	)

	const toggleAt = useCallback(
		(index: number) => {
			const id = idAt(index)
			if (id) checkAt(index, !selectedIds.has(id))
		},
		[idAt, checkAt, selectedIds],
	)

	const selectRange = useCallback(
		(index: number) => {
			const ids = rangeIndices(anchor.current, index)
				.map(idAt)
				.filter((id): id is string => !!id)
			if (anchor.current === null) anchor.current = index
			setSelectedIds((current) => new Set([...current, ...ids]))
		},
		[idAt, setSelectedIds],
	)

	const onRowClick = useCallback(
		(index: number, event: MouseEvent) => {
			if (!canSelect) return
			const action = rowClickAction(event, selectedIds.size > 0)
			if (action === "open") return
			event.preventDefault()
			if (action === "toggle") toggleAt(index)
			else selectRange(index)
		},
		[canSelect, selectedIds.size, toggleAt, selectRange],
	)

	const onRowMouseMove = useCallback(
		(index: number) => {
			if (Date.now() - lastKeyAt.current < MOUSE_IGNORE_MS) return
			cursorFromMouse.current = true
			if (cursorRef.current !== index) setCursor(index)
		},
		[setCursor],
	)

	// A hover cursor follows the pointer out; a keyboard cursor stays put.
	const onListMouseLeave = useCallback(() => {
		if (cursorFromMouse.current) setCursor(null)
	}, [setCursor])

	const extend = (direction: 1 | -1) => {
		if (!canSelect) return
		const from = cursorRef.current
		const to = stepIndex(from, count, direction)
		if (to === null) return
		const ids = [from, to]
			.filter((index): index is number => index !== null && index < count)
			.map(idAt)
			.filter((id): id is string => !!id)
		anchor.current ??= from ?? to
		setSelectedIds((current) => new Set([...current, ...ids]))
		moveCursor(to)
	}

	useShortcut(
		"down, j",
		() => moveCursor(stepIndex(cursorRef.current, count, 1)),
		{ preventDefault: true },
	)
	useShortcut(
		"up, k",
		() => moveCursor(stepIndex(cursorRef.current, count, -1)),
		{ preventDefault: true },
	)
	useShortcut(
		"meta+ArrowDown, ctrl+ArrowDown",
		() => {
			if (count > 0) moveCursor(count - 1)
		},
		{ preventDefault: true },
	)
	useShortcut(
		"meta+ArrowUp, ctrl+ArrowUp",
		() => {
			if (count > 0) moveCursor(0)
		},
		{ preventDefault: true },
	)
	useShortcut("shift+down, shift+j", () => extend(1), { preventDefault: true })
	useShortcut("shift+up, shift+k", () => extend(-1), { preventDefault: true })
	useShortcut("x", () => {
		if (canSelect && cursorRef.current !== null) toggleAt(cursorRef.current)
	})
	useShortcut(
		"enter",
		() => {
			if (cursorRef.current !== null) openAt(cursorRef.current)
		},
		{
			// A focused link or button already acts on Enter.
			ignoreEventWhen: (event) =>
				event.target instanceof HTMLElement &&
				!!event.target.closest("a, button"),
		},
	)
	useEscapeClearsSelection(selectedIds.size > 0, () => {
		anchor.current = null
		onClear()
	})

	return {
		cursor,
		checkAt,
		selectRange,
		onRowClick,
		onRowMouseMove,
		onListMouseLeave,
	}
}
